import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { emailAPI } from '../services/api';
import { useSiteSettings } from '../context/SiteSettingsContext';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { settings } = useSiteSettings();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await emailAPI.requestPasswordReset(email);
      setEmailSent(true);
      toast.success('Email Sent!', {
        description: 'Check your inbox for password reset instructions.'
      });
    } catch (error) {
      console.error('Password reset error:', error);
      // Always show success message for security (don't reveal if email exists)
      setEmailSent(true);
      toast.success('Email Sent!', {
        description: 'If an account exists with this email, you will receive reset instructions.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center py-16 px-4">
        <div className="max-w-md w-full">
          <div className="bg-[#141414] rounded-2xl shadow-xl shadow-[#D4AF37]/10 p-10 border border-[#D4AF37]/20 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h1 className="text-4xl font-bold text-white mb-4 font-heading">Check Your Email</h1>
            <p className="text-lg text-[#888888] mb-4 font-body">
              We've sent password reset instructions to <strong className="text-white">{email}</strong>
            </p>
            <p className="text-sm text-[#888888] mb-8 font-body">
              If you don't receive an email within a few minutes, please contact <a href={`mailto:${settings?.email || 'support@boxverra.com'}`} className="text-[#D4AF37] hover:underline">{settings?.email || 'support@boxverra.com'}</a>
            </p>
            <Link to="/login">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] px-8 py-4 text-lg font-body font-bold">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main flex items-center justify-center py-16 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-soft-lg p-10 border border-soft-sand">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔑</div>
            <h1 className="text-4xl font-bold text-text-heading mb-2 font-heading">Forgot Password?</h1>
            <p className="text-lg text-text-body font-body">No worries, we'll send you reset instructions</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-base font-body text-text-heading">Email Address</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-text-muted" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-12 py-6 text-lg font-body border-2 border-soft-sand focus:border-accent"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-accent hover:bg-accent-hover text-white py-6 text-xl font-bold font-body"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-accent hover:text-accent-hover font-body">
              <ArrowLeft className="h-4 w-4" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;